import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Song} from '../../api/types';

export type PlaylistVisibility = 'private' | 'public';

export interface UserPlaylist {
    id: string;
    name: string;
    img: string;
    tracks: Song[];
    createdAt: number;
    updatedAt: number;
    /** Firebase uid of the creator. Undefined only for legacy guest-only playlists awaiting migration. */
    ownerId?: string;
    visibility: PlaylistVisibility;
}

export interface UserPlaylistsState {
    playlists: Record<string, UserPlaylist>;
    order: string[];
}

const initialState: UserPlaylistsState = {
    playlists: {},
    order: [],
};

export const userPlaylistsSlice = createSlice({
    name: 'userPlaylists',
    initialState,
    reducers: {
        createPlaylist(state, action: PayloadAction<{ id: string; name: string; ownerId?: string; visibility?: PlaylistVisibility }>) {
            const {id, name, ownerId, visibility} = action.payload;
            const now = Date.now();
            state.playlists[id] = {
                id, name, ownerId,
                visibility: visibility ?? 'private',
                img: '', tracks: [],
                createdAt: now, updatedAt: now,
            };
            state.order.unshift(id);
        },
        deletePlaylist(state, action: PayloadAction<string>) {
            const id = action.payload;
            delete state.playlists[id];
            state.order = state.order.filter(pid => pid !== id);
        },
        renamePlaylist(state, action: PayloadAction<{ id: string; name: string }>) {
            const {id, name} = action.payload;
            if (state.playlists[id]) {
                state.playlists[id].name = name;
                state.playlists[id].updatedAt = Date.now();
            }
        },
        addTrack(state, action: PayloadAction<{ playlistId: string; song: Song; sourceImg?: string }>) {
            const {playlistId, song, sourceImg} = action.payload;
            const playlist = state.playlists[playlistId];
            if (playlist && !playlist.tracks.some(t => t.id === song.id)) {
                playlist.tracks.push(song);
                if (!playlist.img) {
                    playlist.img = song.img || sourceImg || '';
                }
                playlist.updatedAt = Date.now();
            }
        },
        removeTrack(state, action: PayloadAction<{ playlistId: string; songId: string }>) {
            const {playlistId, songId} = action.payload;
            const playlist = state.playlists[playlistId];
            if (playlist) {
                playlist.tracks = playlist.tracks.filter(t => t.id !== songId);
                playlist.img = playlist.tracks[0]?.img || '';
                playlist.updatedAt = Date.now();
            }
        },
        reorderTracks(state, action: PayloadAction<{ playlistId: string; fromIndex: number; toIndex: number }>) {
            const {playlistId, fromIndex, toIndex} = action.payload;
            const playlist = state.playlists[playlistId];
            if (playlist) {
                const [moved] = playlist.tracks.splice(fromIndex, 1);
                playlist.tracks.splice(toIndex, 0, moved);
                playlist.updatedAt = Date.now();
            }
        },
        setPlaylistVisibility(state, action: PayloadAction<{ id: string; visibility: PlaylistVisibility }>) {
            const {id, visibility} = action.payload;
            if (state.playlists[id]) {
                state.playlists[id].visibility = visibility;
                state.playlists[id].updatedAt = Date.now();
            }
        },
        // Atomic replace — used by Firestore sync on sign-in / remote updates / sign-out.
        // Not mirrored back to Firestore (see playlistsSyncMiddleware).
        setAllPlaylists(state, action: PayloadAction<UserPlaylistsState>) {
            state.playlists = action.payload.playlists;
            state.order = action.payload.order;
        },
    },
});

export const {
    createPlaylist,
    deletePlaylist,
    renamePlaylist,
    addTrack,
    removeTrack,
    reorderTracks,
    setPlaylistVisibility,
    setAllPlaylists,
} = userPlaylistsSlice.actions;
export default userPlaylistsSlice.reducer;
